import React, { useState } from "react";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import { Box, Typography } from "@mui/material";

import { brand } from "../../themes/theme";

/**
 * Varias series de linea sobre el mismo eje temporal, con leyenda clicable
 * para mostrar/ocultar cada serie (igual patron que el toggle de series del
 * HTML de referencia). Componente "tonto": recibe filas ya traducidas.
 *
 * @param {Array}  data   - [{ x: "M0", <serieKey>: valor, ... }, ...]
 * @param {Array}  series - [{ key, name, color }]
 * @param {number} height
 * @param {Array}  yDomain - [min, max] opcional
 * @param {function} yTickFormatter
 */
function MultiSeriesLineChart({ data, series, height = 260, yDomain, yTickFormatter }) {
    const [hidden, setHidden] = useState(() => new Set());

    const toggle = (key) => {
        setHidden((prev) => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    return (
        <Box>
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={data} margin={{ top: 10, right: 16, bottom: 4, left: -8 }}>
                    <CartesianGrid vertical={false} stroke="rgba(14,43,71,0.08)" />
                    <XAxis
                        dataKey="x"
                        tickLine={false}
                        axisLine={{ stroke: "rgba(14,43,71,0.15)" }}
                        tick={{ fontSize: 11, fill: brand.gray600 }}
                    />
                    <YAxis
                        domain={yDomain || ["auto", "auto"]}
                        tickFormatter={yTickFormatter}
                        tickLine={false}
                        axisLine={false}
                        width={36}
                        tick={{ fontSize: 11, fill: brand.gray600 }}
                    />
                    <Tooltip content={<SeriesTooltip series={series} />} cursor={{ stroke: brand.gray200 }} />
                    {series.map((s) => (
                        <Line
                            key={s.key}
                            type="monotone"
                            dataKey={s.key}
                            name={s.name}
                            stroke={s.color}
                            strokeWidth={2.25}
                            dot={{ r: 3, fill: s.color, stroke: "#fff", strokeWidth: 1 }}
                            activeDot={{ r: 5, fill: s.color, stroke: "#fff", strokeWidth: 2 }}
                            hide={hidden.has(s.key)}
                            isAnimationActive={false}
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                {series.map((s) => {
                    const off = hidden.has(s.key);
                    return (
                        <Box
                            key={s.key}
                            component="button"
                            type="button"
                            onClick={() => toggle(s.key)}
                            sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.6,
                                border: "1px solid",
                                borderColor: "divider",
                                bgcolor: "transparent",
                                borderRadius: 999,
                                px: 1.1,
                                py: 0.4,
                                cursor: "pointer",
                                opacity: off ? 0.4 : 1,
                                transition: "opacity 0.15s ease",
                            }}
                        >
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: s.color, flexShrink: 0 }} />
                            <Typography sx={{ fontSize: 11, color: "text.secondary" }}>{s.name}</Typography>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function SeriesTooltip({ active, payload, label, series }) {
    if (!active || !payload || !payload.length) return null;
    return (
        <Box
            sx={{
                bgcolor: "#fff",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                px: 1.5,
                py: 1,
                boxShadow: "0 4px 16px rgba(10,36,62,0.12)",
            }}
        >
            <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 0.25 }}>{label}</Typography>
            {payload.map((p) => {
                const s = series.find((s2) => s2.key === p.dataKey);
                return (
                    <Typography key={p.dataKey} sx={{ fontSize: 12.5, color: "text.primary" }}>
                        <Box component="span" sx={{ color: p.color, fontWeight: 500 }}>&#9679;</Box> {s?.name || p.dataKey}: {p.value}
                    </Typography>
                );
            })}
        </Box>
    );
}

export default MultiSeriesLineChart;
